var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { barX, defineChart } from "@tanstack/charts";
import { scaleBand } from "@tanstack/charts/scales/band";
import { scaleLinear } from "@tanstack/charts/scales/linear";
import { tooltip } from "@tanstack/charts/tooltip";
import { consume } from "@lit/context";
import { html, LitElement, nothing, unsafeCSS } from "lit";
import { customElement } from "lit/decorators.js";
import { stylePref } from "../../../../constants/prefs.ts";
import { dateOnlyFormat } from "../../../../utils/index.ts";
import { cache } from "../../../../utils/lit.ts";
import base from "../../../../styles/utility/baseline.css?type=raw";
import styles from "./venue.css?type=raw";
import { groupVenueEvents } from "./venue-data.ts";
import { renderSvg } from "../svg.ts";
import gigs from "../gigs.json" with { type: "json" };
import "../../register.ts";
const venues = groupVenueEvents(gigs.events);
const venuesByLabel = new Map(venues.map((row) => [row.label, row]));
const stackVenueLabel = (node) => {
    if (node.kind === "group") {
        return { ...node, children: node.children.map(stackVenueLabel) };
    }
    if (node.kind !== "label")
        return node;
    const venue = venuesByLabel.get(node.text);
    if (!venue)
        return node;
    return {
        kind: "group",
        key: `${node.key}:stacked`,
        children: [
            { ...node, text: venue.venue, y: node.y - 8 },
            {
                ...node,
                key: `${node.key}:city`,
                text: venue.city,
                y: node.y + 8,
                style: { ...node.style, fill: "var(--comment)" },
            },
        ],
    };
};
const renderVenueSvg = (scene, options) => renderSvg({ ...scene, nodes: scene.nodes.map(stackVenueLabel) }, options);
const definition = defineChart({
    margin: { left: 340 },
    marks: [
        barX(venues, {
            id: "gigs-by-venue",
            x: "count",
            y: "label",
            key: "label",
            fill: "var(--gigs-venue-color, var(--button, #0550ae))",
            inset: 4,
        }),
    ],
    scales: {
        x: {
            scale: () => scaleLinear().domain([0, Math.max(...venues.map((row) => row.count))]),
            axis: { label: "Events" },
            grid: true,
        },
        xTop: {
            channel: "x",
            side: "top",
            scale: () => scaleLinear().domain([0, Math.max(...venues.map((row) => row.count))]),
            axis: { label: "Events" },
        },
        y: {
            scale: () => scaleBand().domain(venues.map((row) => row.label)),
        },
    },
    tooltip,
});
const createChartOptions = (pageStyle) => ({
    definition,
    ariaLabel: "Gigs by venue",
    ariaDescription: "Full gig history grouped by venue and city, sorted by event count.",
    height: 80 + venues.length * 48,
    renderSvg: renderVenueSvg,
    renderTooltipBody: ({ points }) => {
        const row = points[0]?.datum;
        if (!row)
            return nothing;
        return html `
      <div class="venue-tooltip caption">
        <strong>${row.label} · ${row.count} event${row.count !== 1 ? "s" : ""}</strong>
        <ul>
          ${row.events.map((event) => html `
              <li>
                <strong>${event.artists.join(", ")}</strong>
                ${event.support?.length
            ? html `<div class="venue-tooltip-support">
                      Support: ${event.support.join(", ")}
                    </div>`
            : null}
                <div>
                  <time datetime=${event.date}>${dateOnlyFormat(event.date, pageStyle)}</time>
                </div>
              </li>
            `)}
        </ul>
      </div>
    `;
    },
});
let GigsByVenue = class GigsByVenue extends LitElement {
    constructor() {
        super(...arguments);
        this.pageStyle = stylePref.fallback;
    }
    static { this.styles = [unsafeCSS(base), unsafeCSS(styles)]; }
    get chartOptions() {
        return createChartOptions(this.pageStyle);
    }
    render() {
        return html `
      <h2 class="headline6">Gigs by venue</h2>
      <p class="caption">Sorted by event count</p>
      <div class="chart">
        <tanstack-chart .options=${this.chartOptions}></tanstack-chart>
      </div>
    `;
    }
};
__decorate([
    consume({ context: stylePref.context, subscribe: true })
], GigsByVenue.prototype, "pageStyle", void 0);
__decorate([
    cache(({ pageStyle }) => [pageStyle])
], GigsByVenue.prototype, "chartOptions", null);
GigsByVenue = __decorate([
    customElement("gigs-by-venue")
], GigsByVenue);
export { GigsByVenue };
